import { PointArray } from '@editor/interfaces/attribute'
import { FrontCameraProfile } from '@editor/interfaces/camera'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export enum SelectionStage {
  DRAW = 'DRAW',
  ROTATE = 'ROTATE',
  FOCUS = 'FOCUS',
  SAVING = 'SAVING',
}

interface BlueprintManagerState {
  /**
   * Selection stage.
   */
  selectionStage: SelectionStage

  /**
   * Plane drawn by user, defined by 3 points.
   */
  drawnPlanePoints: PointArray[]

  /**
   * Preview plane derived from drawPlanePoints, defined by 3 points.
   */
  previewPlanePoints: PointArray[]

  /**
   * Dirty flag.
   */
  isDirty: boolean

  /**
   * Working camera profile while creating a new blueprint.
   */
  workingCameraProfile?: FrontCameraProfile
}

export const initialState: BlueprintManagerState = {
  selectionStage: SelectionStage.DRAW,
  drawnPlanePoints: [],
  previewPlanePoints: [],
  isDirty: false,
}

export const slice = createSlice({
  name: 'cameraProfile',
  initialState,
  reducers: {
    setSelectionStage: (state, action: PayloadAction<SelectionStage>) => {
      state.selectionStage = action.payload
    },
    setWorkingCameraProfile: (state, action: PayloadAction<FrontCameraProfile | undefined>) => {
      state.workingCameraProfile = action.payload
    },
    addPlanePoint: (state, { payload: { point } }: PayloadAction<{ point: PointArray }>) => {
      state.isDirty = true
      state.drawnPlanePoints.push(point)

      if (state.drawnPlanePoints.length === 4) {
        state.selectionStage = SelectionStage.ROTATE
      }
    },
    updateLastPlanePoint: (state, action: PayloadAction<PointArray>) => {
      // If there's no point yet, add it
      if (state.drawnPlanePoints.length === 0) {
        state.drawnPlanePoints.push(action.payload)
      }

      state.drawnPlanePoints[state.drawnPlanePoints.length - 1] = action.payload
    },
    updatePlanePoint: (state, action: PayloadAction<{ index: number; point: PointArray }>) => {
      state.drawnPlanePoints[action.payload.index] = action.payload.point
    },
    undoLastPlanePoint: (state) => {
      state.drawnPlanePoints = state.drawnPlanePoints.slice(0, -1)
    },
    setPreviewPlanePoints: (state, action: PayloadAction<PointArray[]>) => {
      state.previewPlanePoints = action.payload
    },
    resetWorkingState: (state) => {
      state.selectionStage = SelectionStage.DRAW
      state.drawnPlanePoints = []
      state.previewPlanePoints = []
      state.isDirty = false
      state.workingCameraProfile = undefined
    },
    reset: () => initialState,
  },
})

export const {
  setSelectionStage,
  setWorkingCameraProfile,

  addPlanePoint,
  updatePlanePoint,
  updateLastPlanePoint,
  undoLastPlanePoint,
  setPreviewPlanePoints,

  resetWorkingState,
  reset,
} = slice.actions

export default slice.reducer
